import { useMemo, useState } from "react";
import { CalendarDays } from "lucide-react";
import { format } from "date-fns";

import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TarefaStatusBadge } from "@/components/tarefa/TarefaStatusBadge";
import { PersonBadge } from "@/components/shared/PersonBadge";
import { useNavigationHelpers } from "@/lib/navigation-helpers";
import { useTarefaAdminMaisAtrasadas } from "@/hooks/use-tarefa-admin-mais-atrasadas";
import { useTarefaAdminRankingResponsaveis } from "@/hooks/use-tarefa-admin-ranking-responsaveis";
import { TarefaRankingModo } from "@/types/tarefa.type";
import { cn } from "@/lib/utils";

import { TarefaRankedList } from "./TarefaRankedList";

function severidade(atrasoDias: number) {
  if (atrasoDias >= 15) return "border-red-500/30 bg-red-500/[0.06] text-red-700 dark:text-red-300";
  if (atrasoDias >= 5)
    return "border-amber-500/30 bg-amber-500/[0.06] text-amber-700 dark:text-amber-300";
  return "border-border/60 bg-muted/40 text-muted-foreground";
}

function RankingTab({ dias }: { dias: number }) {
  const [modo, setModo] = useState<TarefaRankingModo>("abertas");
  const { ranking, loading } = useTarefaAdminRankingResponsaveis(modo, 10, dias);

  const rows = useMemo(
    () =>
      ranking.map((r) => ({
        key: r.pessoa.id,
        value: r.total,
        label: <PersonBadge pessoa={r.pessoa} isCompact format="short" />,
      })),
    [ranking],
  );

  return (
    <div className="flex h-64 flex-col">
      <div className="mb-2 flex shrink-0 items-center justify-between gap-2">
        <p className="text-xs font-medium text-muted-foreground">
          {modo === "concluidas"
            ? `Mais concluídas nos últimos ${dias} dias`
            : `Mais abertas, criadas nos últimos ${dias} dias`}
        </p>
        <Select value={modo} onValueChange={(v) => setModo(v as TarefaRankingModo)}>
          <SelectTrigger className="h-7 w-[132px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="abertas">Abertas</SelectItem>
            <SelectItem value="concluidas">Concluídas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <Skeleton className="min-h-0 flex-1 rounded-xl" />
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <TarefaRankedList
            rows={rows}
            emptyText={
              modo === "concluidas"
                ? `Nenhuma tarefa concluída nos últimos ${dias} dias.`
                : `Nenhuma tarefa aberta criada nos últimos ${dias} dias.`
            }
          />
        </div>
      )}
    </div>
  );
}

function AtrasadasTab({ dias }: { dias: number }) {
  const { tarefas, loading } = useTarefaAdminMaisAtrasadas(10, dias);
  const { goToTarefaDetails } = useNavigationHelpers();

  if (loading) {
    return (
      <div className="h-64 space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (tarefas.length === 0) {
    return (
      <p className="flex h-64 items-center justify-center text-center text-sm italic text-muted-foreground">
        Nenhuma tarefa atrasada criada nos últimos {dias} dias. 🎉
      </p>
    );
  }

  return (
    <div className="h-64 space-y-2 overflow-y-auto pr-1">
      {tarefas.map((tarefa) => (
        <div
          key={tarefa.id}
          role="button"
          tabIndex={0}
          onClick={() => goToTarefaDetails(tarefa.id)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") goToTarefaDetails(tarefa.id);
          }}
          className="flex cursor-pointer flex-wrap items-center justify-between gap-3 rounded-lg border border-border/60 px-3 py-2.5 text-sm transition-colors hover:bg-muted/30"
        >
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium text-foreground" title={tarefa.nome}>
              {tarefa.nome}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <TarefaStatusBadge status={tarefa.status} />
              {tarefa.atribuidoA && (
                <PersonBadge pessoa={tarefa.atribuidoA} isCompact format="short" />
              )}
              {tarefa.dataPrazo && (
                <span className="flex items-center gap-1">
                  <CalendarDays className="h-3 w-3" />{" "}
                  {format(new Date(tarefa.dataPrazo), "dd/MM/yyyy")}
                </span>
              )}
            </div>
          </div>

          <span
            className={cn(
              "shrink-0 whitespace-nowrap rounded-md border px-2 py-1 text-xs font-semibold",
              severidade(tarefa.atrasoDias),
            )}
          >
            {tarefa.atrasoDias} {tarefa.atrasoDias === 1 ? "dia" : "dias"} atrasada
          </span>
        </div>
      ))}
    </div>
  );
}

export function People({ dias }: { dias: number }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm">
      <Tabs defaultValue="ranking">
        <div className="mb-3 flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-foreground">Pessoas</p>
          <TabsList>
            <TabsTrigger value="ranking">Ranking</TabsTrigger>
            <TabsTrigger value="atrasadas">Atrasadas</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="ranking" className="mt-0">
          <RankingTab dias={dias} />
        </TabsContent>
        <TabsContent value="atrasadas" className="mt-0">
          <AtrasadasTab dias={dias} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
